import { memo } from 'react';
import { TILE_VALUES } from '@scrabble-daily/engine';
import type { PremiumSquare } from '@scrabble-daily/engine';

export interface CellTile {
  letter: string;
  is_blank: boolean;
  staged: boolean;
}

const PREMIUM_STYLES: Record<PremiumSquare, { className: string; short: string; label: string }> = {
  TW: { className: 'bg-board-tw text-white', short: 'TW', label: 'triple word' },
  DW: { className: 'bg-board-dw text-stone-800', short: 'DW', label: 'double word' },
  TL: { className: 'bg-board-tl text-white', short: 'TL', label: 'triple letter' },
  DL: { className: 'bg-board-dl text-stone-800', short: 'DL', label: 'double letter' },
  STAR: { className: 'bg-board-star text-stone-800', short: '★', label: 'centre star' },
};

export type CellHighlight = 'valid' | 'invalid' | null;

interface CellProps {
  row: number;
  col: number;
  premium: PremiumSquare | null;
  tile: CellTile | null;
  /** Live word-check state for cells that are part of a previewed word. */
  highlight?: CellHighlight;
  onTap?: (row: number, col: number) => void;
  testIdPrefix?: string;
  /** Hide the TW/DL/★ text (thumbnail boards keep the colours only). */
  hidePremiumLabel?: boolean;
}

const HIGHLIGHT_RINGS: Record<'valid' | 'invalid', string> = {
  valid: 'ring-2 ring-inset ring-emerald-600',
  invalid: 'ring-2 ring-inset ring-red-500',
};

function CellImpl({
  row,
  col,
  premium,
  tile,
  highlight = null,
  onTap,
  testIdPrefix = '',
  hidePremiumLabel = false,
}: CellProps) {
  const premiumInfo = premium ? PREMIUM_STYLES[premium] : null;
  const contents = tile
    ? `contains ${tile.letter}${tile.is_blank ? ' (blank)' : ` (${TILE_VALUES[tile.letter] ?? 0} points)`}${tile.staged ? ', staged' : ''}${
        highlight ? `, ${highlight === 'valid' ? 'valid word' : 'invalid word'}` : ''
      }`
    : 'empty';
  const label = `Row ${row + 1}, column ${col + 1}${premiumInfo ? `, ${premiumInfo.label}` : ''}, ${contents}`;

  return (
    <button
      type="button"
      data-testid={`${testIdPrefix}cell-${row}-${col}`}
      aria-label={label}
      onClick={() => onTap?.(row, col)}
      // Font sizes are container-query units (cqw) so labels shrink with the
      // board; the max() floors keep them legible at the smallest fit.
      className={`relative flex aspect-square h-full w-full select-none items-center justify-center border border-stone-300/60 text-[max(7px,2.1cqw)] font-semibold leading-none ${
        tile
          ? `${tile.staged ? 'bg-tile-staged' : 'bg-tile-face'} ${
              highlight
                ? HIGHLIGHT_RINGS[highlight]
                : tile.staged
                  ? 'ring-2 ring-inset ring-amber-500'
                  : ''
            }`
          : (premiumInfo?.className ?? 'bg-board-cell')
      }`}
    >
      {tile ? (
        <span className="text-[max(10px,3.6cqw)] font-bold text-stone-900">
          {tile.letter}
          <sub className="ml-px align-sub text-[max(5px,1.6cqw)] font-medium">
            {tile.is_blank ? 0 : (TILE_VALUES[tile.letter] ?? 0)}
          </sub>
        </span>
      ) : (
        <span aria-hidden="true">{hidePremiumLabel ? '' : (premiumInfo?.short ?? '')}</span>
      )}
    </button>
  );
}

export const Cell = memo(CellImpl);
